<template>
  <div :class="'activation-content-wrapper app-activation-wrapper ' + showActivationView">
    <div id="step-bar" class="container-box">
      <div class="step-bar" role="navigation" aria-label="Aktivierungsprozess">
        <div
          class="step"
          @click="goToStepBefore(beforeStepperTariff, infoStepName)"
          v-bind:class="{
            active: showStep(infoStepName),
            'before-active': beforeStepperTariff
          }"
          role="button"
          :aria-current="showStep(infoStepName) ? 'step' : false"
          :tabindex="beforeStepperTariff ? 0 : -1"
          :aria-disabled="!beforeStepperTariff"
        >
          <span class="icon" aria-hidden="true"></span>
          <div>
            <slot name="lblInfoStep"><abbr title="Subscriber Identity Module">SIM</abbr>-Karte freischalten</slot>
          </div>
        </div>
        <div
          class="line"
          v-bind:class="{
            active: showStep(tariffStepName) || showStep(personalDataInputStepName) || showStep(summaryStepName)
          }"
          aria-hidden="true"
        ></div>
        <div
          class="step"
          @click="goToStepBefore(beforeStepperPersonalDataTopUp, tariffStepName)"
          v-bind:class="{
            active: showStep(tariffStepName),
            'before-active': beforeStepperPersonalDataTopUp
          }"
          role="button"
          :aria-current="showStep(tariffStepName) ? 'step' : false"
          :tabindex="beforeStepperPersonalDataTopUp ? 0 : -1"
          :aria-disabled="!beforeStepperPersonalDataTopUp"
        >
          <span class="icon" aria-hidden="true"></span>
          <slot name="lblTariffStep">Tarifauswahl</slot>
        </div>
        <div
          class="line"
          aria-hidden="true"
          v-bind:class="{
            active: showStep(personalDataInputStepName) || showStep(summaryStepName)
          }"
        ></div>
        <div
          class="step"
          @click="goToStepBefore(beforeStepperSummary, personalDataInputStepName)"
          v-bind:class="{
            active: showStep(personalDataInputStepName) || showStep(topUpStepName),
            'before-active': beforeStepperSummary
          }"
          role="button"
          :aria-current="showStep(personalDataInputStepName) ? 'step' : false"
          :tabindex="beforeStepperSummary ? 0 : -1"
          :aria-disabled="!beforeStepperSummary"
        >
          <span class="icon" aria-hidden="true"></span>
          <slot name="lblPersonalDataStep">Deine Kundendaten</slot>
        </div>
        <div class="line" aria-hidden="true" v-bind:class="{ active: showStep(summaryStepName) }"></div>
        <div
          class="step"
          v-bind:class="{ active: showStep(summaryStepName) }"
          role="button"
          :aria-current="showStep(summaryStepName) ? 'step' : false"
          :tabindex="showStep(summaryStepName) ? 0 : -1"
          :aria-disabled="!showStep(summaryStepName)"
        >
          <span class="icon" aria-hidden="true"></span>
          <slot name="lblSummaryStep">Zusammenfassung</slot>
        </div>
      </div>
    </div>
    <ActivationInfo
      v-if="showStep(infoStepName)"
      :activation-code-min="activationCodeMin"
      :activation-code-max="activationCodeMax"
      :next-step="tariffStepName"
    >
      <template v-for="slot in possibleChildSlotsFor('Info')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationInfo>
    <ActivationTariff
      v-if="showStep(tariffStepName)"
      :previous-step="infoStepName"
      :next-step="personalDataInputStepName"
      :extra="extraData.extraData"
      :extra-tariff-options-data="parsedTariffOptionsData.extraTariffOptionsData"
    >
      <template v-for="slot in possibleChildSlotsFor('Tariff')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationTariff>
    <TariffDetails
      v-if="showStep(personalDataInputStepName) || showStep(topUpStepName) || showStep(summaryStepName)"
      :show="true"
      :tariff="selectedTariff"
      :extra-tariff-options-data="parsedTariffOptionsData.extraTariffOptionsData"
      :show-toggle-button="true"
    />
    <ActivationPersonalData
      v-if="showStep(personalDataInputStepName)"
      :alowed-min-age="16"
      :previous-step="tariffStepName"
      :next-step="topUpStepName"
      :show-esim="showEsim"
    >
      <template v-for="slot in possibleChildSlotsFor('PersonalData')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationPersonalData>
    <ActivationTopUpAndPayment
      v-if="showStep(topUpStepName)"
      :credit-card-frame-address="creditCardFrameAddress"
      :credit-card-redirect-url="creditCardRedirectUrl"
      :previous-step="personalDataInputStepName"
      :next-step="summaryStepName"
      :monthly-value="topUpMonthlyValue"
      :selected-tariff="selectedTariff"
    >
      <template v-for="slot in possibleChildSlotsFor('TopUp')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationTopUpAndPayment>
    <ActivationSummary v-if="showStep(summaryStepName)" :previous-step="topUpStepName" next-step="success">
      <template v-for="slot in possibleChildSlotsFor('Summary')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationSummary>
    <ActivationSuccess v-if="showStep(successStepName)">
      <template v-for="slot in possibleChildSlotsFor('Success')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationSuccess>
    <ActivationInfoModal :next-step="tariffStepName">
      <template v-for="slot in possibleChildSlotsFor('InfoModal')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ActivationInfoModal>
  </div>
</template>

<script>
import ActivationInfo, { stepName as infoStepName } from '@/components-v2/flows/LIDL/Activation/Info/ActivationInfo.vue'
import ActivationTariff, { stepName as tariffStepName } from '@/components-v2/flows/LIDL/Activation/Tariff/Tariff.vue'
import ActivationPersonalData, {
  stepName as personalDataInputStepName
} from '@/components-v2/flows/LIDL/Activation/PersonalData/EnterPersonalData.vue'
import ActivationSummary, {
  stepName as summaryStepName
} from '@/components-v2/flows/LIDL/Activation/Summary/ActivationSummary.vue'
import { mapGetters, mapMutations } from 'vuex'
import ActivationTopUpAndPayment, {
  stepName as topUpStepName
} from '@/components-v2/flows/LIDL/Activation/TopUp/EnterTopUpAndPayment.vue'
import ActivationSuccess, {
  stepName as successStepName
} from '@/components-v2/flows/LIDL/Activation/Success/ActivationSuccess.vue'
import transaction from '@/mixins/transaction'
import slotProxy from '@/mixins/slotProxy'
import extraData from '@/mixins/extraData'
import processWrapper from '@/mixins/processWrapper'
import ActivationInfoModal from '../Info/ActivationInfoModal.vue'
import TariffDetails from '../Tariff/TariffDetails.vue'
export default {
  name: 'ActivationWrapper',
  import: {
    infoStepName,
    tariffStepName,
    personalDataInputStepName,
    topUpStepName,
    summaryStepName,
    successStepName
  },
  data() {
    return {
      topUpMonthlyValue: [2]
    }
  },
  mixins: [transaction('activation'), slotProxy(), extraData(), processWrapper('showActivationView')],
  props: {
    creditCardFrameAddress: {
      type: String,
      required: true
    },
    creditCardRedirectUrl: {
      type: String
    },
    otherBrandUrl: {
      type: String
    },
    activationCodeMin: {
      type: Number
    },
    activationCodeMax: {
      type: Number
    },
    showEsim: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    goToStepBefore(active, step) {
      if (active) {
        this.goToStep(step)
      }
    },
    ...mapMutations('activation', ['setBrandUrl', 'goToStep'])
  },
  computed: {
    beforeStepperTariff: function() {
      return this.beforeStepperPersonalDataTopUp || this.showStep(tariffStepName)
    },
    beforeStepperPersonalDataTopUp: function() {
      return this.beforeStepperSummary || this.showStep(personalDataInputStepName) || this.showStep(topUpStepName)
    },
    beforeStepperSummary: function() {
      return this.showStep(summaryStepName)
    },
    selectedTariff: function() {
      return this.savedTariff || {}
    },
    ...mapGetters('activation', ['showActivationView']),
    ...mapGetters('activation/tariff', ['savedTariff'])
  },
  components: {
    ActivationInfoModal,
    ActivationTariff,
    ActivationTopUpAndPayment,
    ActivationSuccess,
    ActivationPersonalData,
    ActivationInfo,
    ActivationSummary,
    TariffDetails
  }
}
</script>
